import type { ContactReferenceTypeValue } from '@/api/address-book/types';
import type {
  DemandIntroducer,
  DemandStatus,
  DemandSummaryRes,
  InterestedPartyType,
} from '@/api/demand/types';

// ==================== Enums ====================

export const OfferStatus = {
  Pending: 'Pending',
  Open: 'Open',
  Closed: 'Closed',
  Cancelled: 'Cancelled',
  Suspended: 'Suspended',
} as const;

export type OfferStatusType = (typeof OfferStatus)[keyof typeof OfferStatus];

export const OfferStructure = {
  Direct: 'Direct',
  Layer1: 'Layer1',
  Layer2: 'Layer2',
} as const;

export type OfferStructureType = (typeof OfferStructure)[keyof typeof OfferStructure];

export const OfferStatusFilter = {
  Active: 'Active',
  Closed: 'Closed',
  All: 'All',
} as const;

export type OfferStatusFilterType = (typeof OfferStatusFilter)[keyof typeof OfferStatusFilter];

/** Whether Gamma has cleared an offer for investors (GP-4704). Derived from the confidence score. */
export const OfferReviewStatus = {
  OnGammaReview: 'OnGammaReview',
  Public: 'Public',
} as const;

export type OfferReviewStatusType = (typeof OfferReviewStatus)[keyof typeof OfferReviewStatus];

export const OfferConfidenceScore = {
  Unassigned: 'Unassigned',
  Unknown: 'Unknown',
  Moderate: 'Moderate',
  High: 'High',
  Confirmed: 'Confirmed',
} as const;

export type OfferConfidenceScoreType =
  (typeof OfferConfidenceScore)[keyof typeof OfferConfidenceScore];

export const OFFER_CONFIDENCE_OPTIONS: OfferConfidenceScoreType[] = [
  OfferConfidenceScore.Unknown,
  OfferConfidenceScore.Moderate,
  OfferConfidenceScore.High,
  OfferConfidenceScore.Confirmed,
];

export const PriceHistoryPeriod = {
  SevenDays: '7D',
  OneMonth: '1M',
  ThreeMonths: '3M',
  SixMonths: '6M',
  OneYear: '1Y',
  TwoYears: '2Y',
  All: 'All',
} as const;

export type PriceHistoryPeriodType = (typeof PriceHistoryPeriod)[keyof typeof PriceHistoryPeriod];

export const CompanyOrderBy = {
  Name: 'Name',
  Valuation: 'Valuation',
  SharePrice: 'SharePrice',
  MinInvestment: 'MinInvestment',
  AvailableSize: 'AvailableSize',
  OffersCount: 'OffersCount',
  RecentlyUpdated: 'RecentlyUpdated',
} as const;

export type CompanyOrderByType = (typeof CompanyOrderBy)[keyof typeof CompanyOrderBy];

export const EntityCompanyOrderBy = {
  Name: 'Name',
  Valuation: 'Valuation',
  SharePrice: 'SharePrice',
  MinTicketSize: 'MinTicketSize',
  TotalAmountAvailable: 'TotalAmountAvailable',
  OffersCount: 'OffersCount',
  SectorName: 'SectorName',
  RecentlyUpdated: 'RecentlyUpdated',
} as const;

export type EntityCompanyOrderByType =
  (typeof EntityCompanyOrderBy)[keyof typeof EntityCompanyOrderBy];

export const OfferOrderBy = {
  Provider: 'Provider',
  SharePrice: 'SharePrice',
  MarketCap: 'MarketCap',
  AvailableSize: 'AvailableSize',
  MinInvestment: 'MinInvestment',
  ClosingDate: 'ClosingDate',
  Structure: 'Structure',
  FeeTerms: 'FeeTerms',
} as const;

export type OfferOrderByType = (typeof OfferOrderBy)[keyof typeof OfferOrderBy];

export const EntityOfferOrderBy = {
  CompanyName: 'CompanyName',
  PricePerShare: 'PricePerShare',
  ImpliedValuation: 'ImpliedValuation',
  AvailableSize: 'AvailableSize',
  MinInvestment: 'MinInvestment',
  ExpiresAt: 'ExpiresAt',
} as const;

export type EntityOfferOrderByType = (typeof EntityOfferOrderBy)[keyof typeof EntityOfferOrderBy];

export const AdminOfferOrderBy = {
  Company: 'Company',
  Provider: 'Provider',
  SharePrice: 'SharePrice',
  MarketCap: 'MarketCap',
  AvailableSize: 'AvailableSize',
  FillHistory: 'FillHistory',
  MinInvestment: 'MinInvestment',
  ClosingDate: 'ClosingDate',
  Structure: 'Structure',
  FeeTerms: 'FeeTerms',
  ConfidenceScore: 'ConfidenceScore',
  Seller: 'Seller',
  Status: 'Status',
  PostedDate: 'PostedDate',
} as const;

export type AdminOfferOrderByType = (typeof AdminOfferOrderBy)[keyof typeof AdminOfferOrderBy];

export const OrderDirection = {
  Asc: 'Asc',
  Desc: 'Desc',
} as const;

export type OrderDirectionType = (typeof OrderDirection)[keyof typeof OrderDirection];

// ==================== Shared ====================

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface RangeFilter {
  min: number;
  max: number;
}

export interface FilterOption {
  value: string;
  label: string;
}

// ==================== Sectors ====================

export interface SectorDto {
  id: string;
  name: string;
  description: string;
  rank: number;
  subSectorCount: number;
  subSectors?: SubSectorDto[];
}

export interface SubSectorDto {
  id: string;
  name: string;
  sectorId: string;
  sectorIds?: string[];
  rank: number;
}

export interface SectorListRes {
  sectors: SectorDto[];
  total: number;
}

export interface SubSectorListRes {
  subSectors: SubSectorDto[];
  total: number;
}

// ==================== Sector Filter Options ====================

export interface SectorFilterOption {
  id: string;
  name: string;
}

export interface SubSectorFilterOption {
  id: string;
  name: string;
  sectorId: string;
  sectorIds?: string[];
}

// ==================== Fees ====================

export interface FeeComponent {
  managementFee: number;
  performanceFee: number;
  upfrontFee: number;
}

export interface ExtendedFees {
  netFeesToGp: FeeComponent;
  sellSideFees: FeeComponent;
  totalBuyerFees: FeeComponent;
  gammaPrimeSplit: number;
  feeTermsNote: string | null;
}

// ==================== Offers ====================

export interface OfferDto {
  id: string;
  companyId: string;
  companyName: string;
  providerId: string;
  providerName: string;
  status: OfferStatusType;
  structure: OfferStructureType;
  pricePerShare: number;
  minInvestment: number;
  availableSize: number;
  originalSize: number;
  marketCap: number;
  extendedFees: ExtendedFees;
  feeTermsDisplay: string;
  description: string | null;
  closingDate: string | null;
  createdAt: string;
  updatedAt: string;
  confidenceScore: OfferConfidenceScoreType | null;
  seller: OfferSellerDto | null;
  introducers: OfferIntroducerDto[] | null;
  postedBy: OfferPostedByDto | null;
}

export interface OfferPostedByDto {
  id: string;
  name: string;
}

export interface OfferListItemDto {
  id: string;
  provider: string;
  entityId: string;
  sharePrice: number | null;
  marketCap: number | null;
  availableSize: number;
  originalSize: number;
  fillPercentage: number;
  minInvestment: number | null;
  closingDate: string | null;
  structure: OfferStructureType;
  feeTerms: string;
  feeTermsNote?: string | null;
  status?: OfferStatusType;
  isMy?: boolean;
  reviewStatus?: OfferReviewStatusType | null;
  confidenceScore: OfferConfidenceScoreType | null;
  seller: OfferSellerDto | null;
  introducers: OfferIntroducerDto[] | null;
}

export interface OfferFilters {
  marketCap: RangeFilter;
  availableSize: RangeFilter;
  minInvestment: RangeFilter;
  structures: FilterOption[];
}

export interface OfferListRes {
  offers: OfferListItemDto[];
  pagination: PaginationMeta;
  filters: OfferFilters;
}

export interface OfferContactRefDto {
  type: ContactReferenceTypeValue | null;
  id: string;
  name?: string | null;
}

export type OfferSellerDto = OfferContactRefDto;
export type OfferIntroducerDto = OfferContactRefDto;

export interface OfferContactRefOption {
  id: string;
  name: string | null;
}

export interface AdminOfferFilters {
  introducers: OfferContactRefOption[];
  sellers: OfferContactRefOption[];
}

export interface AdminOfferListItemDto {
  companyId: string;
  companyName: string;
  companyLogoImageId: string | null;
  providerId: string;
  sharePrice: number | null;
  marketCap: number | null;
  availableSize: number;
  originalSize: number;
  fillPercentage: number;
  minInvestment: number | null;
  closingDate: string | null;
  structure: OfferStructureType;
  feeTermsDisplay: string;
  status: OfferStatusType;
  confidenceScore: OfferConfidenceScoreType;
  seller: OfferSellerDto | null;
  introducers: OfferIntroducerDto[];
  createdAt: string;
  // Present so row actions (view/edit/fill/close/delete) can target the offer.
  id: string;
  reviewStatus: OfferReviewStatusType | null;
  /** The introducer who submitted this Gamma-owned offer; null on offers Gamma staff posted. */
  viaIntroducer: OfferContactRefDto | null;
  /** Posted by Gamma staff (GP-4704 Q4): Gamma-owned and not an introducer submission. */
  isMy: boolean;
}

export interface AdminOffersRes {
  offers: AdminOfferListItemDto[];
  pagination: PaginationMeta;
  filters: AdminOfferFilters;
}

export interface GetAdminOffersParams {
  page?: number;
  limit?: number;
  search?: string;
  /** Only offers Gamma staff posted, resolved server-side. */
  mine?: boolean;
  companyIds?: string[];
  providerIds?: string[];
  status?: OfferStatusFilterType;
  structures?: OfferStructureType[];
  confidenceScores?: string[];
  introducerIds?: string[];
  sellerIds?: string[];
  orderBy?: AdminOfferOrderByType;
  orderDirection?: OrderDirectionType;
}

// ==================== Marketplace offers (entity Supply) ====================

/**
 * The sorts the marketplace list accepts. Confidence, Seller and Introducer are withheld on other
 * providers' rows, so the server refuses sorting by them.
 */
export const MARKETPLACE_OFFER_ORDER_BY: readonly AdminOfferOrderByType[] = [
  AdminOfferOrderBy.PostedDate,
  AdminOfferOrderBy.ClosingDate,
  AdminOfferOrderBy.AvailableSize,
  AdminOfferOrderBy.SharePrice,
  AdminOfferOrderBy.Company,
];

/** An offer row of `GET /entity/companies/marketplace-offers` (BE-1, GP-4679). */
export interface MarketplaceOfferListItemDto {
  id: string;
  companyId: string;
  companyName: string;
  companyLogoImageId: string | null;
  /** The provider's name, resolved by the server. */
  provider: string;
  entityId: string;
  sharePrice: number | null;
  marketCap: number | null;
  availableSize: number;
  originalSize: number;
  fillPercentage: number;
  minInvestment: number | null;
  closingDate: string | null;
  structure: OfferStructureType;
  feeTerms: string;
  status: OfferStatusType;
  /** Posted by the session's entity (Gamma staff for an admin session). */
  isMy: boolean;
  /** Gamma-only: null for every entity row, own rows included. */
  confidenceScore: OfferConfidenceScoreType | null;
  /** The author's view of the review; null on other providers' rows. */
  reviewStatus: OfferReviewStatusType | null;
  viaIntroducer: OfferContactRefDto | null;
  /** Null on other providers' rows: the server withholds them. */
  seller: OfferSellerDto | null;
  introducers: OfferIntroducerDto[] | null;
  createdAt: string;
}

export interface MarketplaceOfferFilters {
  /** Companies with at least one offer the caller may see. */
  companies: { id: string; name: string }[];
  statuses: FilterOption[];
  structures: FilterOption[];
  availableSize: RangeFilter;
  minInvestment: RangeFilter;
}

export interface MarketplaceOffersRes {
  offers: MarketplaceOfferListItemDto[];
  pagination: PaginationMeta;
  filters: MarketplaceOfferFilters;
}

export interface GetMarketplaceOffersParams {
  page?: number;
  limit?: number;
  search?: string;
  /** Only the session entity's own offers. */
  mine?: boolean;
  companyIds?: string[];
  /** Omitted: other providers' Open offers plus every own offer, whatever its status. */
  statuses?: OfferStatusType[];
  structures?: OfferStructureType[];
  orderBy?: AdminOfferOrderByType;
  orderDirection?: OrderDirectionType;
}

// ==================== Fill History ====================

export interface FillEntryDto {
  amount: number;
  percentageOfTotal: number;
  newAvailableSize: number;
  filledAt: string;
}

export interface SectorTagDto {
  id: string;
  name: string;
}

export interface OfferFillHistoryRes {
  offerId: string;
  companyId: string;
  companyName: string;
  logoImageId: string | null;
  sectorTags: SectorTagDto[];
  originalSize: number;
  availableSize: number;
  totalFilled: number;
  fillPercentage: number;
  entries: FillEntryDto[];
}

// ==================== Companies (public) ====================

export type ValueSource = 'Offer' | 'MostRecent' | 'None';

export interface CompanyCardDto {
  id: string;
  name: string;
  legalName: string | null;
  logoImageId: string | null;
  sectorTags: SectorTagDto[];
  valuation: number | null;
  valuationSource: ValueSource;
  sharePrice: number | null;
  sharePriceSource: ValueSource;
  isLastRoundData: boolean;
  priceChange: number | null;
  priceChangePercent: number | null;
  minInvestment: number | null;
  availableSize: number | null;
  offersCount: number;
  bestStructure?: OfferStructureType | null;
  isFollowed: boolean;
}

export interface CompaniesFilters {
  sectors: SectorFilterOption[];
  subSectors: SubSectorFilterOption[];
  availableSize: RangeFilter;
  minInvestment: RangeFilter;
  dealStatuses: FilterOption[];
  structures: FilterOption[];
}

export interface CompanyListRes {
  companies: CompanyCardDto[];
  pagination: PaginationMeta;
  filters: CompaniesFilters;
}

export interface CompanyDetailDto {
  id: string;
  name: string;
  legalName: string | null;
  description: string | null;
  logoImageId: string | null;
  website: string | null;
  foundedYear: number;
  headquarters: string;
  sectorId: string;
  sectorName: string;
  subSectorId: string;
  subSectorName: string;
  additionalSubSectorIds: string[];
  additionalSubSectorNames: string[];
  allSectorTags: SectorTagDto[];
  currentSharePrice: number | null;
  mostRecentValuation: number | null;
  mostRecentSharePrice: number | null;
  isLastRoundData: boolean;
  priceChange: number | null;
  priceChangePercent: number | null;
  minTicketSize: number | null;
  totalAvailableSize: number | null;
  isFollowed: boolean;
  offers: OfferDto[];
  activeOffersCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CompanySummaryDto {
  id: string;
  entityId: string;
  name: string;
  legalName: string;
  description: string | null;
  logoImageId: string | null;
  website: string | null;
  headquarters: string;
  sectorTags: SectorTagDto[];
  valuation: number | null;
  currentSharePrice: number | null;
  isLastRoundData: boolean;
  priceChange: number | null;
  priceChangePercent: number | null;
  minTicketSize: number | null;
  totalAmountAvailable: number | null;
  isFollowed: boolean;
}

// ==================== Price History ====================

export interface PricePointDto {
  date: string;
  price: number;
  marketCap?: number;
  source?: string;
}

export interface PriceHistorySummaryDto {
  currentPrice: number;
  priceChange: number | null;
  priceChangePercent: number | null;
  highPrice: number;
  lowPrice: number;
  averagePrice: number;
  fiftyTwoWeekHigh: number;
  fiftyTwoWeekLow: number;
}

export interface PriceHistoryRes {
  companyId: string;
  companyName: string;
  period: PriceHistoryPeriodType;
  summary: PriceHistorySummaryDto;
  priceHistory: PricePointDto[];
  dataPointsCount: number;
  legalName: string;
}

// ==================== Market Depth ====================

export interface MarketDepthSideSummaryDto {
  totalAmount: number;
  entryCount: number;
  levelCount: number;
}

export interface MarketDepthLevelDto {
  valuation: number;
  demandAmount?: number;
  demandEntryCount?: number;
  demandCumulative?: number;
  supplyAmount?: number;
  supplyOfferCount?: number;
  supplyCumulative?: number;
}

export interface MarketDepthRes {
  companyId: string;
  demand: MarketDepthSideSummaryDto;
  supply: MarketDepthSideSummaryDto;
  levels: MarketDepthLevelDto[];
}

// ==================== Entity Companies ====================

export interface EntityCompanyListItemDto {
  id: string;
  name: string;
  legalName: string | null;
  logoImageId: string | null;
  valuation: number | null;
  sharePrice: number | null;
  minTicketSize: number | null;
  totalAmountAvailable: number | null;
  offersCount: number;
  sectorId: string;
  sectorName: string;
}

export interface EntityCompanyFilters {
  sectors: SectorFilterOption[];
  subSectors: SubSectorFilterOption[];
  availableSize: RangeFilter;
  minInvestment: RangeFilter;
  dealStatuses: FilterOption[];
  structures: FilterOption[];
}

export interface EntityCompanyListRes {
  companies: EntityCompanyListItemDto[];
  pagination: PaginationMeta;
  filters: EntityCompanyFilters;
}

// ==================== Entity Offers ====================

export interface EntityOfferListItemDto {
  id: string;
  companyId: string;
  companyName: string;
  companyLegalName: string | null;
  pricePerShare: number;
  marketCap: number;
  availableSize: number;
  originalSize: number;
  fillPercentage: number;
  minInvestment: number;
  closingDate: string | null;
  feeTermsDisplay: string;
  status: OfferStatusType;
  structure: OfferStructureType;
  activatedAt: string | null;
  confidenceScore: OfferConfidenceScoreType | null;
  seller: OfferSellerDto | null;
  introducers: OfferIntroducerDto[];
}

export interface CompanyFilterOption {
  id: string;
  name: string;
}

export interface EntityOfferFilters {
  statuses: FilterOption[];
  structures: FilterOption[];
  companies: CompanyFilterOption[];
}

export interface EntityOfferListRes {
  offers: EntityOfferListItemDto[];
  pagination: PaginationMeta;
  filters: EntityOfferFilters;
}

// ==================== Entity Dashboard ====================

export interface EntityDashboardRes {
  activeOffers: number;
  openInquiries: number;
  companies: number;
}

// ==================== Offer Stats ====================

export interface OffersStatsRes {
  totalOffers: number;
}

// ==================== Query Params ====================

export interface GetCompaniesParams {
  page?: number;
  limit?: number;
  search?: string;
  sectorIds?: string[];
  subSectorIds?: string[];
  minAvailableSize?: number;
  maxAvailableSize?: number;
  minInvestment?: number;
  maxInvestment?: number;
  dealStatuses?: string[];
  structures?: OfferStructureType[];
  followedOnly?: boolean;
  orderBy?: CompanyOrderByType;
  orderDirection?: OrderDirectionType;
}

export interface GetOffersParams {
  page?: number;
  limit?: number;
  providerSearch?: string;
  companyId?: string;
  companyIds?: string[];
  statuses?: OfferStatusType[];
  structures?: OfferStructureType[];
  minMarketCap?: number;
  maxMarketCap?: number;
  minAvailableSize?: number;
  maxAvailableSize?: number;
  minInvestment?: number;
  maxInvestment?: number;
  orderBy?: OfferOrderByType;
  orderDirection?: OrderDirectionType;
}

export interface GetEntityCompaniesParams {
  page?: number;
  limit?: number;
  search?: string;
  sectorIds?: string[];
  subSectorIds?: string[];
  minAvailableSize?: number;
  maxAvailableSize?: number;
  minInvestment?: number;
  maxInvestment?: number;
  dealStatuses?: string[];
  structures?: OfferStructureType[];
  orderBy?: EntityCompanyOrderByType;
  orderDirection?: OrderDirectionType;
  includeAll?: boolean;
}

export interface GetEntityOffersParams {
  page?: number;
  limit?: number;
  search?: string;
  statuses?: OfferStatusType[];
  structures?: OfferStructureType[];
  companyIds?: string[];
  introducerIds?: string[];
  sellerIds?: string[];
  confidenceScores?: OfferConfidenceScoreType[];
  orderBy?: EntityOfferOrderByType;
  orderDirection?: OrderDirectionType;
}

export interface GetAdminCompaniesParams {
  page?: number;
  limit?: number;
  search?: string;
  sectorIds?: string[];
  subSectorIds?: string[];
  minAvailableSize?: number;
  maxAvailableSize?: number;
  minInvestment?: number;
  maxInvestment?: number;
  dealStatuses?: string[];
  structures?: OfferStructureType[];
  orderBy?: EntityCompanyOrderByType;
  orderDirection?: OrderDirectionType;
}

// ==================== Mutation Payloads ====================

// ==================== Company Validation ====================

export interface ValidateCompanyFields {
  name?: string;
  legalName?: string;
}

export interface ValidateCompanyBody {
  companyId?: string;
  fields: ValidateCompanyFields;
}

export interface CompanyValidationResult {
  name: boolean;
  legalName: boolean;
}

export interface CompanyValidationRes {
  result: CompanyValidationResult;
}

// ==================== Mutation Payloads ====================

export interface CreateCompanyPayload {
  name: string;
  legalName: string;
  description?: string;
  logoImageId?: string;
  website?: string;
  foundedYear: number;
  headquarters: string;
  sectorId: string;
  subSectorId: string;
  additionalSubSectorIds?: string[];
  mostRecentValuation?: number;
  mostRecentSharePrice?: number;
}

export interface UpdateCompanyPayload {
  name?: string;
  legalName?: string;
  description?: string;
  logoImageId?: string | null;
  website?: string;
  foundedYear?: number;
  headquarters?: string;
  sectorId?: string;
  subSectorId?: string;
  additionalSubSectorIds?: string[];
  mostRecentValuation?: number | null;
  mostRecentSharePrice?: number | null;
}

export interface CreateOfferFeeComponent {
  managementFee: number;
  performanceFee: number;
  upfrontFee: number;
}

export interface CreateOfferExtendedFees {
  netFeesToGp: CreateOfferFeeComponent;
  sellSideFees: CreateOfferFeeComponent;
  gammaPrimeSplit: number;
  feeTermsNote?: string;
}

export interface CreateOfferPayload {
  companyId: string;
  structure: OfferStructureType;
  pricePerShare?: number;
  minInvestment: number;
  availableSize: number;
  originalSize: number;
  marketCap: number;
  extendedFees: CreateOfferExtendedFees;
  description?: string;
  closingDate?: string;
  confidenceScore?: OfferConfidenceScoreType;
  seller?: OfferSellerDto;
  introducers?: OfferIntroducerDto[];
}

export interface AdminUpdateOfferPayload {
  structure: OfferStructureType;
  minInvestment?: number;
  availableSize?: number;
  originalSize?: number;
  marketCap: number;
  extendedFees: CreateOfferExtendedFees;
  description?: string;
  closingDate?: string;
  seller?: OfferSellerDto;
  introducers?: OfferIntroducerDto[];
}

export interface FillOfferPayload {
  fillAmount: number;
}

export interface CreateInquiryPayload {
  message?: string;
}

export interface AdminDeleteOfferPayload {
  reason?: string;
}

export interface IdRes {
  id: string;
}

export interface FillOfferRes {
  remainingAvailableSize: number;
}

export const ReviewStatus = {
  OnGammaReview: 'OnGammaReview',
  Public: 'Public',
} as const;

export type ReviewStatusType = (typeof ReviewStatus)[keyof typeof ReviewStatus];

export interface IntroducerOfferListItemDto extends OfferListItemDto {
  companyId: string;
  companyName: string;
  companyLogoImageId: string | null;
  reviewStatus: ReviewStatusType | null;
}

export interface IntroducerOfferListRes {
  offers: IntroducerOfferListItemDto[];
  pagination: PaginationMeta;
}

export interface IntroducerDemandEntryDto {
  id: string;
  companyId: string;
  companyName: string;
  interestedPartyType: InterestedPartyType;
  interestedPartyName: string;
  amount: number;
  targetValuation: number;
  status: DemandStatus;
  reviewStatus: ReviewStatusType;
  creatorCapacity: string | null;
  creatorCapacityNote: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IntroducerDemandListRes {
  entries: IntroducerDemandEntryDto[];
  pagination: PaginationMeta;
}

export interface IntroducerCompanyDemandEntryDto {
  id: string;
  companyId: string;
  amount: number;
  targetValuation: number;
  createdAt: string;
  isMine: boolean;
  interestedPartyName: string | null;
  interestedPartyType: InterestedPartyType | null;
  introducers: DemandIntroducer[] | null;
  reviewStatus: ReviewStatusType | null;
}

export interface IntroducerCompanyDemandRes {
  entries: IntroducerCompanyDemandEntryDto[];
  summary: DemandSummaryRes;
  pagination: PaginationMeta;
}

export interface GetIntroducerSubmissionsParams {
  page?: number;
  limit?: number;
}
